import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from './baseQuery';
import { StudentProfileResponse } from '../../features/profiles/types/profileTypes';

export interface StudentClass {
  classId: number;
  courseId: number;
  name: string;
  desc: string;
  fees: number;
  grade: string;
  tutorId: number;
  tutorName: string;
  enrolledDate: string;
  enrolledSlot: string;
  subject: string;
  imagePath: string | null;
  location?: 'online' | 'in person';
  address?: string;
}

interface GetStudentAllClassesResponse {
  statusCode: number;
  data: {
    count: number;
    arrayObj: StudentClass[];
  };
}

export interface UpdateStudentProfileRequest {
  userType: 'student';
  basicInfo: {
    firstName: string;
    lastName: string;
    grade: string;
    school: string;
    parentid: number;
  };
}

export const studentApi = createApi({
  reducerPath: 'studentApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['StudentProfile'],
  endpoints: builder => ({
    getStudentProfile: builder.query<StudentProfileResponse['data'], {studentId: number}>({
      query: ({studentId}) => ({
        url: `/students/${studentId}`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: StudentProfileResponse) => response.data,
      providesTags: (result, error, {studentId}) => [{ type: 'StudentProfile', id: studentId }],
    }),
    updateStudentProfile: builder.mutation<StudentProfileResponse, {studentId: number; data: UpdateStudentProfileRequest}>({
      query: ({studentId, data}) => ({
        url: `/students/${studentId}`,
        method: 'PUT',
        headers: {
          accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: data,
      }),
      invalidatesTags: (result, error, {studentId}) => [{ type: 'StudentProfile', id: studentId }],
    }),
    getStudentAllClasses: builder.query<StudentClass[], {studentId: number}>({
      query: ({studentId}) => ({
        url: `/students/${studentId}/allclasses`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetStudentAllClassesResponse) => response.data?.arrayObj || [],
    }),
    getStudentUpcomingClasses: builder.query<StudentClass[], {studentId: number}>({
      query: ({studentId}) => ({
        url: `/students/${studentId}/upcomingclasses`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetStudentAllClassesResponse) => response.data?.arrayObj || [],
    }),
  }),
});

export const {
  useGetStudentProfileQuery,
  useUpdateStudentProfileMutation,
  useGetStudentAllClassesQuery,
  useGetStudentUpcomingClassesQuery,
} = studentApi;
